/**
 * useMessaging — React Query hooks for messaging REST API
 *
 * Adapted from bachata-nation-next useMessaging.ts.
 */

import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { messagingApi } from '../api';
import type { Conversation, ChatMessage } from '../types';

// Query key factory
export const messagingKeys = {
  all: ['messaging'] as const,
  conversations: () => [...messagingKeys.all, 'conversations'] as const,
  conversation: (id: string) => [...messagingKeys.all, 'conversation', id] as const,
  messages: (conversationId: string) => [...messagingKeys.all, 'messages', conversationId] as const,
  starred: (conversationId: string) => [...messagingKeys.all, 'starred', conversationId] as const,
  pinned: (conversationId: string) => [...messagingKeys.all, 'pinned', conversationId] as const,
  thread: (conversationId: string, messageId: string) => [...messagingKeys.all, 'thread', conversationId, messageId] as const,
  channels: () => [...messagingKeys.all, 'channels'] as const,
  globalStarred: () => [...messagingKeys.all, 'globalStarred'] as const,
  presence: (userIds: string) => [...messagingKeys.all, 'presence', userIds] as const,
};

// =============================================================================
// Conversations
// =============================================================================

export function useConversations() {
  return useQuery({
    queryKey: messagingKeys.conversations(),
    queryFn: () => messagingApi.getConversations(),
    select: (data) => data.conversations,
    staleTime: 10 * 1000, // 10s — real-time updates come via useGlobalNotifications WS
    refetchInterval: 60 * 1000, // 60s safety-net poll — WS handles instant updates
  });
}

export function useArchivedConversations() {
  return useQuery({
    queryKey: [...messagingKeys.conversations(), 'archived'],
    queryFn: () => messagingApi.getConversations({ archived: true }),
    select: (data) => data.conversations,
    staleTime: 30 * 1000,
  });
}

export function useConversation(id: string | null) {
  return useQuery({
    queryKey: messagingKeys.conversation(id || ''),
    queryFn: () => messagingApi.getConversation(id!),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useCreateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: messagingApi.createConversation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

export function useLeaveConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) => messagingApi.leaveConversation(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) => messagingApi.deleteConversation(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

export function useUpdateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; description?: string; avatar?: string }) =>
      messagingApi.updateConversation(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversation(variables.id) });
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

// =============================================================================
// Messages (Infinite Query)
// =============================================================================

interface MessagesPage {
  messages: ChatMessage[];
  hasMore: boolean;
  oldestSequenceId: number | null;
}

export function useMessages(conversationId: string | null) {
  return useInfiniteQuery<MessagesPage>({
    queryKey: messagingKeys.messages(conversationId || ''),
    queryFn: ({ pageParam }) =>
      messagingApi.getMessages(conversationId!, {
        beforeSequenceId: pageParam as number | undefined,
        limit: 50,
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore || !lastPage.oldestSequenceId) return undefined;
      return lastPage.oldestSequenceId;
    },
    enabled: !!conversationId,
    staleTime: 5 * 60 * 1000, // 5 min — real-time updates come via WebSocket
    gcTime: 10 * 60 * 1000,   // 10 min — keep in memory for back-navigation
  });
}

/**
 * Optimistically mark a conversation as read in the conversations cache.
 * Call this immediately after sendRead() so the unread badge clears instantly
 * without waiting for the next server refetch.
 */
export function useMarkConversationReadInCache() {
  const queryClient = useQueryClient();

  return (conversationId: string, upToSequenceId: number) => {
    queryClient.setQueryData<{ conversations: Conversation[] }>(
      messagingKeys.conversations(),
      (old) => {
        if (!old) return old;
        return {
          ...old,
          conversations: old.conversations.map(c =>
            c.id === conversationId
              ? { ...c, unreadCount: 0, lastReadSeq: upToSequenceId }
              : c
          ),
        };
      }
    );
  };
}

/**
 * Mark own messages as read in the messages cache when the peer sends a read receipt.
 * Sets isRead=true on all own messages with sequenceId <= upToSequenceId.
 */
export function useMarkMessagesReadInCache() {
  const queryClient = useQueryClient();

  return (conversationId: string, upToSequenceId: number, currentUserId: string) => {
    queryClient.setQueryData<InfiniteData<MessagesPage>>(
      messagingKeys.messages(conversationId),
      (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) =>
              msg.senderId === currentUserId && msg.sequenceId <= upToSequenceId && !msg.isRead
                ? { ...msg, isRead: true }
                : msg
            ),
          })),
        };
      }
    );
  };
}

/**
 * Helper to add a new message to the messages cache (from WebSocket)
 */
export function useAddMessageToCache() {
  const queryClient = useQueryClient();

  return (conversationId: string, message: ChatMessage) => {
    queryClient.setQueryData<InfiniteData<MessagesPage>>(
      messagingKeys.messages(conversationId),
      (old) => {
        if (!old) return old;

        const lastPage = old.pages[old.pages.length - 1];
        if (!lastPage) return old;

        // Check if message already exists
        for (const page of old.pages) {
          if (page.messages.some(m => m.id === message.id)) return old;
        }

        // Add to last page
        return {
          ...old,
          pages: [
            ...old.pages.slice(0, -1),
            {
              ...lastPage,
              messages: [...lastPage.messages, message],
            },
          ],
        };
      }
    );

    // Update conversation list (lastMessage, unread)
    queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
  };
}

/**
 * Helper to update a message in cache (edit, delete, reaction)
 */
export function useUpdateMessageInCache() {
  const queryClient = useQueryClient();

  return (conversationId: string, messageId: string, updater: (msg: ChatMessage) => ChatMessage) => {
    queryClient.setQueryData<InfiniteData<MessagesPage>>(
      messagingKeys.messages(conversationId),
      (old) => {
        if (!old) return old;

        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            messages: page.messages.map(msg =>
              msg.id === messageId ? updater(msg) : msg
            ),
          })),
        };
      }
    );
  };
}

// =============================================================================
// Search + Starred
// =============================================================================

export function useSearchMessages(conversationId: string) {
  return useMutation({
    mutationFn: (params: {
      q: string;
      sender?: string;
      from?: string;
      to?: string;
      limit?: number;
      offset?: number;
    }) => messagingApi.searchMessages(conversationId, params),
  });
}

export function useStarredMessages(conversationId: string | null) {
  return useQuery({
    queryKey: messagingKeys.starred(conversationId || ''),
    queryFn: () => messagingApi.getStarredMessages(conversationId!),
    enabled: !!conversationId,
    select: (data) => data.messages,
  });
}

export function useGlobalStarredMessages() {
  return useQuery({
    queryKey: messagingKeys.globalStarred(),
    queryFn: () => messagingApi.getGlobalStarredMessages(),
    select: (data) => data.messages,
  });
}

export function usePinnedMessages(conversationId: string | null) {
  return useQuery({
    queryKey: messagingKeys.pinned(conversationId || ''),
    queryFn: () => messagingApi.getPinnedMessages(conversationId!),
    enabled: !!conversationId,
    select: (data) => data.messages,
  });
}

export function useThreadReplies(conversationId: string | null, messageId: string | null) {
  return useQuery({
    queryKey: messagingKeys.thread(conversationId || '', messageId || ''),
    queryFn: () => messagingApi.getThreadReplies(conversationId!, messageId!),
    enabled: !!conversationId && !!messageId,
  });
}

// =============================================================================
// Participants
// =============================================================================

export function useAddParticipants() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, participantIds }: { conversationId: string; participantIds: string[] }) =>
      messagingApi.addParticipants(conversationId, participantIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversation(variables.conversationId) });
    },
  });
}

export function useRemoveParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, userId }: { conversationId: string; userId: string }) =>
      messagingApi.removeParticipant(conversationId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversation(variables.conversationId) });
    },
  });
}

// =============================================================================
// Channels
// =============================================================================

export function useChannels() {
  return useQuery({
    queryKey: messagingKeys.channels(),
    queryFn: () => messagingApi.getChannels(),
    select: (data) => data.channels,
    staleTime: 60 * 1000,
  });
}

export function useCreateChannel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: messagingApi.createChannel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.channels() });
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

export function useJoinChannel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) => messagingApi.joinChannel(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.channels() });
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

export function useUpdateParticipantRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, userId, role }: { conversationId: string; userId: string; role: string }) =>
      messagingApi.updateParticipant(conversationId, { userId, role }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversation(variables.conversationId) });
    },
  });
}

// =============================================================================
// Presence
// =============================================================================

export function usePresenceQuery(userIds: string[]) {
  const key = userIds.sort().join(',');

  return useQuery({
    queryKey: messagingKeys.presence(key),
    queryFn: () => messagingApi.getPresence(userIds),
    enabled: userIds.length > 0,
    refetchInterval: 10000, // Poll every 10s
    select: (data) => data.presence,
  });
}

// =============================================================================
// Unread Count (computed from conversations)
// =============================================================================

export function useTotalUnreadCount(): number {
  const { data: conversations } = useConversations();
  if (!conversations) return 0;
  return conversations.reduce((sum, c) => sum + c.unreadCount, 0);
}

// =============================================================================
// Helper utilities
// =============================================================================

export function getConversationDisplayName(conversation: Conversation, currentUserId: string): string {
  if (conversation.name) return conversation.name;

  if (conversation.type === 'dm') {
    const other = conversation.participants.find(p => p.userId !== currentUserId);
    return other?.user.name || 'User';
  }

  return 'Group';
}

export function getConversationAvatar(conversation: Conversation, currentUserId: string): string | null {
  if (conversation.avatar) return conversation.avatar;

  if (conversation.type === 'dm') {
    const other = conversation.participants.find(p => p.userId !== currentUserId);
    return other?.user.avatar || null;
  }

  return null;
}

// =============================================================================
// Mark All as Read
// =============================================================================

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await messagingApi.markAllAsRead();
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

// =============================================================================
// Mark Conversation as Unread
// =============================================================================

export function useMarkConversationUnread() {
  const queryClient = useQueryClient();

  return (conversationId: string) => {
    // Optimistic update
    queryClient.setQueryData<{ conversations: Conversation[] }>(
      messagingKeys.conversations(),
      (old) => {
        if (!old) return old;
        return {
          ...old,
          conversations: old.conversations.map(c =>
            c.id === conversationId
              ? { ...c, unreadCount: Math.max(c.unreadCount, 1) }
              : c
          ),
        };
      }
    );

    // Persist to server
    messagingApi.updateParticipant(conversationId, {
      markedUnreadAt: new Date().toISOString(),
    }).catch(() => {
      // Revert on failure
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    });
  };
}

// =============================================================================
// Mute Conversation
// =============================================================================

export function useMuteConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, isMuted }: { conversationId: string; isMuted: boolean }) =>
      messagingApi.updateParticipant(conversationId, { isMuted }),
    onMutate: async ({ conversationId, isMuted }) => {
      // Optimistic update
      queryClient.setQueryData<{ conversations: Conversation[] }>(
        messagingKeys.conversations(),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            conversations: old.conversations.map(c =>
              c.id === conversationId ? { ...c, isMuted } : c
            ),
          };
        }
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

// =============================================================================
// Archive Conversation
// =============================================================================

export function useArchiveConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, isArchived }: { conversationId: string; isArchived: boolean }) =>
      messagingApi.updateParticipant(conversationId, { isArchived }),
    onMutate: async ({ conversationId, isArchived }) => {
      // Optimistic: remove from current list
      queryClient.setQueryData<{ conversations: Conversation[] }>(
        messagingKeys.conversations(),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            conversations: old.conversations.map(c =>
              c.id === conversationId ? { ...c, isArchived } : c
            ),
          };
        }
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

// =============================================================================
// Forward Message
// =============================================================================

export function useForwardMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { messageId: string; conversationIds: string[]; additionalMessage?: string }) => {
      const response = await messagingApi.forwardMessage(params);
      return response;
    },
    onSuccess: (_data, variables) => {
      // Invalidate conversation list so last message updates
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
      queryClient.invalidateQueries({ queryKey: messagingKeys.channels() });
      // Invalidate messages for each target conversation
      for (const convId of variables.conversationIds) {
        queryClient.invalidateQueries({ queryKey: messagingKeys.messages(convId) });
      }
    },
  });
}
